import React from 'react';
import Modal from '#/common/components/Modal';
import TMMButton from '../../TMMButton';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import Form, { type FormItem, type FormRefType } from '#/common/components/Form';
import type { FieldDataChangedEvent } from 'devextreme/ui/form';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';

export interface RelatedItemsCreateModalFieldDef {
  dataField: string;
  label?: string | false;
  editorType?: string;
  editorOptions?: Record<string, unknown>;
  required?: boolean;
  isRequired?: boolean;
  cssClass?: string;
  validationRules?: any[];
}

export interface RelatedItemsCreateModalConfig {
  title: string;
  width?: number;
  fields?: RelatedItemsCreateModalFieldDef[];
  initialValues?: Record<string, unknown>;
  infoMessage?: string;
  cancelLabel?: string;
  primaryLabel?: string;
  onPrimaryAction?: (args: {
    values: Record<string, unknown>;
    close: () => void;
  }) => void | Promise<void>;
}

export interface RelatedItemsCreateModalProps {
  visible: boolean;
  modal: RelatedItemsCreateModalConfig;
  onClose: () => void;
}

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 16px 16px;
  position: relative;

  .dx-form,
  .dx-layout-manager,
  .dx-field-item,
  .dx-field-item-content,
  .dx-texteditor,
  .dx-dropdowneditor,
  .dx-numberbox {
    width: 100% !important;
    max-width: 100% !important;
  }

  .tmm-section-no-background {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const InfoMessage = styled.div`
  background: ${color('custom', 'constantBlue', 0.12)};
  border: 1px solid ${color('custom', 'constantBlue', 0.35)};
  color: ${color('primary', 'dark')};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
`;

const LoaderWrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${color('custom', 'invertedBackground', 0.45)};
  pointer-events: all;
  border-radius: 12px;
`;

const RelatedItemsCreateModal: React.FC<RelatedItemsCreateModalProps> = ({
  visible,
  modal,
  onClose,
}) => {
  const validationGroup = `RelatedItemsCreate-${React.useId()}`;
  const [values, setValues] = React.useState<Record<string, unknown>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const formRef = React.useRef<FormRefType | null>(null);

  const formValues = React.useMemo(
    () => ({ ...modal.initialValues, ...values }),
    [modal.initialValues, values],
  );

  const modalFormFields = React.useMemo<FormItem<Record<string, unknown>>[]>(() => {
    const safeFields = Array.isArray(modal.fields)
      ? modal.fields
          .filter((field): field is RelatedItemsCreateModalFieldDef => !!field)
          .map((field) => {
            const fieldValue = modal.initialValues?.[field.dataField];
            const editorOptions = {
              ...field.editorOptions,
              ...(fieldValue ? { value: fieldValue } : {}),
            };

            return {
              ...field,
              editorOptions,
              isRequired: field.isRequired ?? field.required,
            };
          })
      : [];

    return [
      {
        colCount: 1,
        cssClass: 'tmm-section-no-background tmm-section-no-reset-margin-padding',
        items: safeFields as any,
      },
    ];
  }, [modal.fields, modal.initialValues]);

  React.useEffect(() => {
    if (visible) {
      setValues({});
      setIsSubmitting(false);
    }
  }, [visible, modal.title, modal.initialValues]);

  const handleClose = React.useCallback(
    (force = false) => {
      if (isSubmitting && !force) return;
      setIsSubmitting(false);
      setValues({});
      onClose();
    },
    [isSubmitting, onClose],
  );

  const handleOnHiding = React.useCallback(
    (e?: { cancel?: boolean }) => {
      if (isSubmitting) {
        if (e) e.cancel = true;
        return;
      }
      handleClose();
    },
    [isSubmitting, handleClose],
  );

  const onFieldDataChanged = React.useCallback((e: FieldDataChangedEvent) => {
    const field = e?.dataField;
    if (!field) return;
    setValues((prev) => ({ ...prev, [field]: e.value }));
  }, []);

  const isCreateDisabled = React.useMemo(() => {
    const requiredFields = (modal.fields ?? []).filter(
      (field) => !!field && (field.isRequired || field.required),
    );

    if (!requiredFields.length) return false;

    return requiredFields.some((field) => {
      const value = formValues[field.dataField];

      if (typeof value === 'string') {
        return value.trim().length === 0;
      }

      return value === null || value === undefined;
    });
  }, [modal.fields, formValues]);

  const handlePrimaryAction = React.useCallback(async () => {
    if (isSubmitting) return;

    const instance = formRef.current?.instance();
    const validation = instance?.validate();
    if (validation && !validation.isValid) return;

    const formData = (instance?.option('formData') as Record<string, unknown>) ?? formValues;

    try {
      setIsSubmitting(true);

      if (modal.onPrimaryAction) {
        await modal.onPrimaryAction({ values: formData, close: () => handleClose(true) });
        return;
      }

      handleClose(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [modal, formValues, handleClose, isSubmitting]);

  const defaultContent = (
    <FieldsContainer>
      <Form<Record<string, unknown>>
        key={`${visible ? 'related-items-create-open' : 'related-items-create-closed'}-${JSON.stringify(modal.initialValues ?? {})}`}
        ref={formRef}
        record={formValues}
        fields={modalFormFields}
        validationGroup={validationGroup}
        onFieldDataChanged={onFieldDataChanged}
      />
      {modal.infoMessage ? <InfoMessage>{modal.infoMessage}</InfoMessage> : null}
      {isSubmitting ? (
        <LoaderWrap>
          <StandardThrobber />
        </LoaderWrap>
      ) : null}
    </FieldsContainer>
  );

  const defaultActions = [
    <TMMButton
      className="width-50"
      key="cancel-create-item-modal"
      label={modal.cancelLabel || 'Cancel'}
      type="normal"
      disabled={isSubmitting}
      onClick={() => handleClose()}
    />,
    <TMMButton
      className="width-50"
      key="create-item-modal"
      label={isSubmitting ? 'Creating...' : modal.primaryLabel || 'Create'}
      type="submit"
      disabled={isCreateDisabled || isSubmitting}
      onClick={handlePrimaryAction}
    />,
  ];

  return (
    <Modal
      visible={visible}
      title={modal.title}
      width={modal.width ?? 520}
      dragEnabled
      hideCloseButton={isSubmitting}
      hideOnOutsideClick={!isSubmitting}
      onHiding={handleOnHiding}
      content={defaultContent}
      actions={defaultActions}
    />
  );
};

export default React.memo(RelatedItemsCreateModal);
